'use client'

import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/next'
import { Menu } from 'lucide-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import React from 'react'

import { SharedTopNav } from '@/components/shared-top-nav'
import { AppSidebar } from '@/components/sidebar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  SidebarInset,
  SidebarProvider
} from '@/components/ui/sidebar'
import { ExecutionProvider, useExecution } from '@/contexts/ExecutionContext'
import { MobileSidebarProvider, useMobileSidebar } from '@/contexts/MobileSidebarContext'
import { NewsModalProvider } from '@/contexts/NewsModalContext'
import { PanelProvider } from '@/contexts/PanelContext'
import { SidebarContextProvider, useSidebar } from '@/contexts/SidebarContext'
import { TopNavTitleProvider } from '@/contexts/TopNavTitleContext'
import { WorkspaceContextProvider } from '@/contexts/WorkspaceContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useBreakpoints } from '@/hooks/use-breakpoints'
import { READ_ONLY_TEAM_ERROR_EVENT, type ReadOnlyTeamErrorDetail } from '@/utils/api-error-handler'
import { appendWorkspaceScopeToPath, getPersistedActiveTeamId, normalizeTeamId } from '@/utils/workspace-scope'

import { AgentTopNavRight } from './agent/components/AgentTopNavRight'
import { AgentHistoryPanelProvider } from './agent/contexts/AgentHistoryPanelContext'
import { MobileSheetProvider, useMobileSheet } from './MobileSheetContext'
import { NewsModalGate } from './NewsModalGate'

export default function Layout({ children }: { children: React.ReactNode }) {
  return (
      <MobileSheetProvider>
        <MobileSidebarProvider>
          <SidebarContextProvider>
            <WorkspaceContextProvider>
              <PanelProvider>
                <ExecutionProvider>
                  <TopNavTitleProvider>
                    <NewsModalProvider>
                      <AgentHistoryPanelProvider>
                        <React.Suspense fallback={null}>
                          <NewsModalGate />
                        </React.Suspense>
                        <React.Suspense fallback={null}>
                          <LayoutContent>
                            {children}
                          </LayoutContent>
                        </React.Suspense>
                      </AgentHistoryPanelProvider>
                    </NewsModalProvider>
                  </TopNavTitleProvider>
                </ExecutionProvider>
              </PanelProvider>
            </WorkspaceContextProvider>
          </SidebarContextProvider>
        </MobileSidebarProvider>
      </MobileSheetProvider>
  )
}

function LayoutContent({
  children,
}: {
  children: React.ReactNode
}) {
  const { open, setOpen } = useSidebar()
  const { selectedExecution: _selectedExecution, setSelectedExecution: _setSelectedExecution, executions: _executions } =
    useExecution()
  const { setIsMobileSheetOpen: _setIsMobileSheetOpen } = useMobileSheet()
  const { isSm: _isSm, isMd, isXl, isLg: _isLg, isReady: isBreakpointReady } = useBreakpoints()
  const { setIsMobileSidebarOpen: _setIsMobileSidebarOpen } = useMobileSidebar()
  const { activeTeamId, setActiveTeamId } = useWorkspace()
  const [readOnlyError, setReadOnlyError] = React.useState<ReadOnlyTeamErrorDetail | null>(null)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const p = String(pathname || '')
  const sharedParam = String(searchParams.get('shared') || '').toLowerCase()
  const isSharedMode = sharedParam === '1' || sharedParam === 'true' || sharedParam === 'yes' || sharedParam === 'on'
  const isPublicJobShareRoute =
    p === '/mol/new' && String(searchParams.get('from') || '').toLowerCase() === 'jobs'
  const isPublicPy2dMolJobShareRoute =
    p === '/py2dmol/new' &&
    String(searchParams.get('from') || '').toLowerCase() === 'jobs' &&
    isSharedMode
  const isPublicCodeJobShareRoute =
    p === '/code/new' &&
    String(searchParams.get('from') || '').toLowerCase() === 'jobs' &&
    isSharedMode
  const isPublicMolOpenmmShareRoute =
    p === '/mol/new' &&
    String(searchParams.get('from') || '').toLowerCase() === 'openmm_workflow' &&
    isSharedMode
  const isPublicSequencesShareRoute = /^\/sequences\/[^/]+\/[^/]+$/.test(p) && isSharedMode
  const isPublicJobAliasShareRoute = /^\/job\/[^/]+$/.test(p) && isSharedMode
  const isPublicEvollaResultsShareRoute = /^\/evolla\/results\/[^/]+$/.test(p) && isSharedMode
  const isPublicWorkflowComposerShareRoute = /^\/workflow\/composer\/[^/]+$/.test(p) && isSharedMode
  const isPublicPy2dMolOpenmmShareRoute =
    p === '/py2dmol/new' &&
    String(searchParams.get('from') || '').toLowerCase() === 'openmm_workflow' &&
    isSharedMode
  const isPublicCodeOpenmmShareRoute =
    p === '/code/new' &&
    String(searchParams.get('from') || '').toLowerCase() === 'openmm_workflow' &&
    isSharedMode
  const hasSharedWorkflowId = String(searchParams.get('workflow_id') || searchParams.get('workflowId') || '').trim().length > 0
  const isPublicMolLibraryShareRoute =
    /^\/mol\/[^/]+$/.test(p) &&
    String(searchParams.get('from') || '').toLowerCase() === 'library' &&
    isSharedMode &&
    hasSharedWorkflowId
  const isPublicCodeLibraryShareRoute =
    /^\/code\/[^/]+$/.test(p) &&
    String(searchParams.get('from') || '').toLowerCase() === 'library' &&
    isSharedMode &&
    hasSharedWorkflowId
  const isPublicAppsRoute = p === '/apps'
  const isPublicAgentSkillsRoute = p === '/agent/skills'
  const isPublicAgentCliRoute = p === '/agent/cli'
  const isProjectAgentsIdeEntryRoute = /^\/projects\/[^/]+\/agents-ide$/.test(p)
  const isAgentRoute =
    p.startsWith('/agent') ||
    p.startsWith('/agent-ide') ||
    p.startsWith('/new-agent') ||
    p.startsWith('/v2/agent-ide') ||
    p.startsWith('/v2/new-agent') ||
    isProjectAgentsIdeEntryRoute
  /** Agent surfaces with history toggle in top nav */
  const isAgentChatPage =
    p === '/agent' ||
    p === '/new-agent' ||
    p === '/v2/new-agent' ||
    isProjectAgentsIdeEntryRoute ||
    /^\/agent\/chats\/[^/]+$/.test(p) ||
    /^\/agent-ide\/session\/[^/]+$/.test(p) ||
    /^\/v2\/agent-ide\/session\/[^/]+$/.test(p)
  const isAssistantUiBaseSessionRoute = /^\/agents\/[^/]+\/?$/.test(p)
  const isJobsRoute = p.startsWith('/jobs')
  const isCodeRoute = p.startsWith('/code')
  const isWidePaddedNewPage =
    p.startsWith('/fold/new') ||
    p.startsWith('/design/new') ||
    p.startsWith('/properties/new') ||
    // Match the same outer padding as /fold/new
    p.startsWith('/sequences/') ||
    // Keep /job/{id} alias route aligned with sequences page padding
    p.startsWith('/job/') ||
    // Match /fold/new padding for tasks results pages
    p.startsWith('/tasks/results') ||
    // Match /fold/new padding for templates page
    p.startsWith('/workflows/templates') ||
    // Match /fold/new padding for models page
    p.startsWith('/models') ||
    // Match /models padding for integrations pages
    p.startsWith('/integrations') ||
    // Match /models padding for settings page
    p.startsWith('/settings') ||
    p.startsWith('/invoices') ||
    // Match /models padding for projects page
    p.startsWith('/projects') ||
    // Redirect route used by Projects CTA
    p.startsWith('/project')
  const isProjectsListPage = p === '/projects'
  const isProjectDetailPage = p.startsWith('/projects/') && p !== '/projects/new'
  const isTemplatesModelsOrSettings =
    p === '/workflows/templates' ||
    p.startsWith('/models') ||
    p.startsWith('/settings') ||
    p.startsWith('/integrations') ||
    p.startsWith('/invoices')

  // Only auto-collapse sidebar when transitioning from XL to smaller breakpoints
  // This prevents auto-collapse on initial load
  const prevIsXl = React.useRef(isXl)
  React.useEffect(() => {
    // Only collapse if we're transitioning from XL to smaller AND sidebar is open
    if (prevIsXl.current && !isXl && open) {
      setOpen(false)
    }
    prevIsXl.current = isXl
  }, [isXl, open, setOpen])

  // Keep workspace context synchronized with URL scope.
  const hasHydratedStoredWorkspaceRef = React.useRef(false)
  React.useEffect(() => {
    if (isPublicJobShareRoute || isPublicPy2dMolJobShareRoute || isPublicCodeJobShareRoute || isPublicMolOpenmmShareRoute || isPublicSequencesShareRoute || isPublicJobAliasShareRoute || isPublicEvollaResultsShareRoute || isPublicWorkflowComposerShareRoute || isPublicPy2dMolOpenmmShareRoute || isPublicCodeOpenmmShareRoute || isPublicMolLibraryShareRoute || isPublicCodeLibraryShareRoute || isPublicAppsRoute || isPublicAgentSkillsRoute || isPublicAgentCliRoute) return
    const currentPath = pathname || '/'
    const currentQuery = searchParams.toString()
    const currentUrl = currentQuery ? `${currentPath}?${currentQuery}` : currentPath
    const urlTeamId = normalizeTeamId(searchParams.get('team_id'))
    if (urlTeamId) {
      if (urlTeamId !== activeTeamId) {
        setActiveTeamId(urlTeamId)
      }
      hasHydratedStoredWorkspaceRef.current = true
      return
    }

    // Only restore from persisted storage once on initial load.
    if (!hasHydratedStoredWorkspaceRef.current) {
      hasHydratedStoredWorkspaceRef.current = true
      const storedTeamId = getPersistedActiveTeamId()
      if (storedTeamId) {
        const scopedUrl = appendWorkspaceScopeToPath(currentUrl, storedTeamId)
        if (scopedUrl !== currentUrl) {
          router.replace(scopedUrl)
          return
        }
        if (storedTeamId !== activeTeamId) {
          setActiveTeamId(storedTeamId)
        }
        return
      }
    }

    // No team in URL (after initial restore) means personal workspace.
    if (activeTeamId !== null) {
      setActiveTeamId(null)
    }
  }, [activeTeamId, isPublicJobShareRoute, isPublicPy2dMolJobShareRoute, isPublicCodeJobShareRoute, isPublicMolOpenmmShareRoute, isPublicSequencesShareRoute, isPublicJobAliasShareRoute, isPublicEvollaResultsShareRoute, isPublicWorkflowComposerShareRoute, isPublicPy2dMolOpenmmShareRoute, isPublicCodeOpenmmShareRoute, isPublicMolLibraryShareRoute, isPublicCodeLibraryShareRoute, isPublicAppsRoute, isPublicAgentSkillsRoute, isPublicAgentCliRoute, pathname, router, searchParams, setActiveTeamId])

  React.useEffect(() => {
    if (isPublicJobShareRoute || isPublicPy2dMolJobShareRoute || isPublicCodeJobShareRoute || isPublicMolOpenmmShareRoute || isPublicSequencesShareRoute || isPublicJobAliasShareRoute || isPublicEvollaResultsShareRoute || isPublicWorkflowComposerShareRoute || isPublicPy2dMolOpenmmShareRoute || isPublicCodeOpenmmShareRoute || isPublicMolLibraryShareRoute || isPublicCodeLibraryShareRoute || isPublicAppsRoute || isPublicAgentSkillsRoute || isPublicAgentCliRoute) return
    const onReadOnlyError = (event: Event) => {
      const custom = event as CustomEvent<ReadOnlyTeamErrorDetail>
      if (!custom.detail) return
      setReadOnlyError(custom.detail)
    }
    window.addEventListener(READ_ONLY_TEAM_ERROR_EVENT, onReadOnlyError as EventListener)
    return () => {
      window.removeEventListener(READ_ONLY_TEAM_ERROR_EVENT, onReadOnlyError as EventListener)
    }
  }, [isPublicJobShareRoute, isPublicPy2dMolJobShareRoute, isPublicCodeJobShareRoute, isPublicMolOpenmmShareRoute, isPublicSequencesShareRoute, isPublicJobAliasShareRoute, isPublicEvollaResultsShareRoute, isPublicWorkflowComposerShareRoute, isPublicPy2dMolOpenmmShareRoute, isPublicCodeOpenmmShareRoute, isPublicMolLibraryShareRoute, isPublicCodeLibraryShareRoute, isPublicAppsRoute, isPublicAgentSkillsRoute, isPublicAgentCliRoute])

  return (
    <SidebarProvider open={open} onOpenChange={setOpen}>
      <AppSidebar />
      <SidebarInset className="relative flex h-dvh min-h-0 flex-col overflow-hidden transition-all duration-300 ease-out">
        {!isAssistantUiBaseSessionRoute ? (
          <SharedTopNav
            left={
              isBreakpointReady && !isMd ? (
                <Button variant="ghost" size="icon" onClick={() => _setIsMobileSidebarOpen(true)}>
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Open sidebar</span>
                </Button>
              ) : null
            }
            right={
              isBreakpointReady && isAgentChatPage && isMd ? (
                <AgentTopNavRight />
              ) : null
            }
          />
        ) : null}

        <div
          className={[
            'flex min-h-0 flex-1 flex-col',
            // Aura chat pages manage their own internal scrolling (messages list), so avoid an outer page scroller.
            isAgentRoute
              ? 'overflow-hidden'
              : isCodeRoute
                ? // Code editor manages scrolling inside tree + content panes.
                  'overflow-hidden'
              : isJobsRoute
                ? // Jobs page has its own internal scroll + pull-to-refresh.
                  'overflow-hidden pt-2'
              : isWidePaddedNewPage
                ? // Match padding across these pages; 5× current horizontal padding (px-4 → md:px-20).
                  // Double that again (md:px-20 → md:px-40). No bottom padding on projects list so list fills height.
                  // Projects list, project detail, templates, models, settings: no horizontal padding at lg and below; padding at xl and above.
                  `overflow-auto gap-4 pt-0 ${isProjectsListPage ? 'px-0 xl:px-40 pb-0' : isProjectDetailPage || isTemplatesModelsOrSettings ? 'px-0 xl:px-40 pb-4' : 'px-4 md:px-40 pb-4'}`
                : 'overflow-auto gap-4 p-4 pt-0 xs:p-0'
          ].join(' ')}
        >
          <div className="min-h-0 flex-1">{children}</div>
          <Dialog open={Boolean(readOnlyError)} onOpenChange={(open) => (!open ? setReadOnlyError(null) : undefined)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Read-only access</DialogTitle>
                <DialogDescription>
                  {readOnlyError?.teamName
                    ? `You have read-only permission for team ${readOnlyError.teamName}.`
                    : 'You have read-only permission for this team.'}
                  {' '}
                  {readOnlyError?.ownerEmail
                    ? `Ask the owner (${readOnlyError.ownerEmail}) to upgrade your access.`
                    : 'Ask the team owner to upgrade your access.'}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => setReadOnlyError(null)}>OK</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Analytics debug={false} />
          <SpeedInsights />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}